import React from 'react';
import {
  ToastContainer as BaseToastContainer,
  Id,
  Slide,
  ToastContentProps,
  toast as toastify,
} from 'react-toastify';
import { MuiIconButton } from '@ui/base_library';
import { makeStyles } from 'tss-react/mui';
import palette from '@ui/palette';
import Icon from '@ui/Icon';
import Typography from '@ui/Typography';
import LoadingIcon from '../LoadingIndicators/CircularLoader';
import CtaToast, { ToastProps } from './CtaToast';

const DEFAULT_AUTO_CLOSE = 3000;

const useStyles = makeStyles({ name: 'Toaster' })(theme => ({
  container: {
    minWidth: '20%',
    maxWidth: '35%',
    display: 'flex',
    justifyContent: 'space-between',
  },
  toast: {
    padding: 0,
    display: 'flex',
    backgroundColor: palette.gray_800,
    alignItems: 'center',
    borderRadius: theme.spacing(0.5),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    color: palette.gray_100,
    width: 'fit-content',
  },
  dismiss: {
    fontSize: '1rem',
    '&:hover': {
      backgroundColor: palette.gray_500,
    },
    marginLeft: theme.spacing(1),
    borderRadius: theme.spacing(0.5),
  },
  successIcon: {
    backgroundColor: palette.mint_300,
  },
  errorIcon: {
    backgroundColor: palette.punch_300,
  },
  loadingSpinner: {
    marginRight: theme.spacing(2),
  },
  iconContainer: {
    borderRadius: '50%',
    justifyContent: 'center',
    height: 24,
    width: 24,
    display: 'flex',
    alignItems: 'center',
  },
  overrides: {
    '.Toastify__close-button:hover, .Toastify__close-button:focus': {
      backgroundColor: 'red',
    },
  },
}));

export const CloseButton = ({ closeToast }: { closeToast: () => void }) => {
  const { classes } = useStyles();

  return (
    <MuiIconButton
      className={classes.dismiss}
      aria-label="close"
      onClick={closeToast}
    >
      <Icon type="ex" htmlColor="white" />
    </MuiIconButton>
  );
};

const SuccessIcon = () => {
  const { classes } = useStyles();
  return (
    <div className={`${classes.iconContainer} ${classes.successIcon}`}>
      <Icon type="check" htmlColor="black" fontSize="small" />
    </div>
  );
};

const ErrorIcon = () => {
  const { classes } = useStyles();
  return (
    <div className={`${classes.iconContainer} ${classes.errorIcon}`}>
      <Icon type="exclamation02" htmlColor="black" fontSize="medium" />
    </div>
  );
};

const LoadingSpinner = () => {
  const { classes } = useStyles();
  return (
    <div className={classes.loadingSpinner}>
      <LoadingIcon size={20} />
    </div>
  );
};

type ToastOptions = 'success' | 'error' | 'loading';
export const toast = (options: {
  message: string;
  title?: string;
  type?: ToastOptions;
  autoClose?: number | false;
  closeOnClick?: boolean;
  icon?: false;
  cta?: {
    label: string;
    onClick: () => void;
  };
}) => {
  const toastContent = (toastProps: ToastContentProps<ToastProps>) => {
    if (options?.cta) {
      return (
        <CtaToast
          {...toastProps}
          data={{
            message: options.message,
            callToAction: options.cta,
          }}
        />
      );
    }
    if (options.title) {
      return (
        <div>
          <Typography variant="pMd" bold>
            {options.title}
          </Typography>
          <Typography variant="pSm" color="gray_75">
            {options.message}
          </Typography>
        </div>
      );
    }
    return <Typography variant="pMd">{options.message}</Typography>;
  };

  const toastifyOptions = {
    autoClose: options.autoClose,
    closeOnClick: options.closeOnClick,
    icon: options.icon,
  };

  if (options.type === 'error') {
    return toastify.error(
      toastProps => toastContent(toastProps as ToastContentProps<ToastProps>),
      toastifyOptions
    );
  }
  if (options.type === 'loading') {
    return toastify.loading(
      toastProps => toastContent(toastProps as ToastContentProps<ToastProps>),
      toastifyOptions
    );
  }
  return toastify.success(
    toastProps => toastContent(toastProps as ToastContentProps<ToastProps>),
    toastifyOptions
  );
};

export const dismissToast = (toastId: Id) => {
  toastify.dismiss(toastId);
};

export const updateToast = (
  toastId: Id,
  options: {
    message: string;
    type?: 'success' | 'error';
    autoClose?: number;
  }
) => {
  toastify.update(toastId, {
    render: <Typography variant="pMd">{options.message}</Typography>,
    type: options.type || 'success',
    autoClose: options.autoClose || DEFAULT_AUTO_CLOSE,
    isLoading: false, // Necessary to kick loading toast out of loading state.
    closeButton: null, // Forces use of default close button.
  });
};

const Toaster = () => {
  const { classes } = useStyles();
  return (
    <div className={classes.overrides}>
      <BaseToastContainer
        position="bottom-left"
        autoClose={DEFAULT_AUTO_CLOSE}
        hideProgressBar
        closeButton={({ closeToast }) => (
          <CloseButton closeToast={closeToast} />
        )}
        className={classes.container}
        toastClassName={classes.toast}
        transition={Slide}
        // eslint-disable-next-line react/no-unstable-nested-components
        icon={({ type, isLoading }) => {
          switch (type) {
            case 'error':
              return <ErrorIcon />;
            case 'success':
              return <SuccessIcon />;
            default:
              return isLoading ? <LoadingSpinner /> : null;
          }
        }}
      />
    </div>
  );
};

export default Toaster;
